import React, { useCallback, useRef, useState } from "react";

import { useTranslation } from "react-i18next";
import { CSSTransition, SwitchTransition } from "react-transition-group";

import Button from "./Button";
import LicensePhoto from "./LicensePhoto";
import styles from "./LicensePhotoModal.module.css";

interface LicensePhotoModalProps {
  isOpen: boolean;
  onClose: () => void;
  onComplete: (frontPhoto: Blob, backPhoto: Blob) => void;
  className?: string;
}

enum Step {
  Front = 0,
  Back = 1,
  Complete = 2,
}

const LicensePhotoModal = ({
  isOpen,
  onClose,
  onComplete,
  className = "",
}: LicensePhotoModalProps) => {
  const { t } = useTranslation();
  const nodeRef = useRef<HTMLDivElement>(null);
  const [currentStep, setCurrentStep] = useState<Step>(Step.Front);
  const [frontPhoto, setFrontPhoto] = useState<Blob | null>(null);
  const [backPhoto, setBackPhoto] = useState<Blob | null>(null);

  const handleFrontPhotoConfirm = useCallback((photo: Blob) => {
    setFrontPhoto(photo);
    setCurrentStep(Step.Back);
  }, []);

  const handleBackPhotoConfirm = useCallback((photo: Blob) => {
    setBackPhoto(photo);
    setCurrentStep(Step.Complete);
  }, []);

  const handleComplete = useCallback(() => {
    if (frontPhoto && backPhoto) {
      onComplete(frontPhoto, backPhoto);
      onClose();
    }
  }, [frontPhoto, backPhoto, onComplete, onClose]);

  const handleClose = useCallback(() => {
    setCurrentStep(Step.Front);
    setFrontPhoto(null);
    setBackPhoto(null);
    onClose();
  }, [onClose]);

  const handleBack = useCallback(() => {
    if (currentStep === Step.Back) {
      setCurrentStep(Step.Front);
      setFrontPhoto(null);
      setBackPhoto(null);
    } else if (currentStep === Step.Complete) {
      setCurrentStep(Step.Back);
      setBackPhoto(null);
    }
  }, [currentStep]);

  if (!isOpen) return null;

  return (
    <div
      className={`fixed inset-0 z-50 flex items-center justify-center ${className}`}
    >
      <div
        className="absolute inset-0 bg-black bg-opacity-50"
        onClick={handleClose}
      />

      <div className="relative bg-white rounded-lg p-6 max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto overflow-x-hidden">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-semibold text-blue">
            {currentStep === Step.Front &&
              t("waiver.licenseModal.frontTitle", "Front License")}
            {currentStep === Step.Back &&
              t("waiver.licenseModal.backTitle", "Back License")}
            {currentStep === Step.Complete &&
              t("waiver.licenseModal.reviewTitle", "Review Photos")}
          </h3>
          <button
            onClick={handleClose}
            className="text-gray-500 hover:text-gray-700 text-2xl font-bold"
          >
            ×
          </button>
        </div>

        <div className="flex items-center justify-center mb-6">
          <div className="flex items-center space-x-2">
            <div
              className={`w-3 h-3 rounded-full ${currentStep >= Step.Front ? "bg-blue" : "bg-gray-300"}`}
            />
            <div
              className={`w-8 h-1 ${currentStep > Step.Front ? "bg-blue" : "bg-gray-300"}`}
            />
            <div
              className={`w-3 h-3 rounded-full ${currentStep >= Step.Back ? "bg-blue" : "bg-gray-300"}`}
            />
            <div
              className={`w-8 h-1 ${currentStep === Step.Complete ? "bg-blue" : "bg-gray-300"}`}
            />
            <div
              className={`w-3 h-3 rounded-full ${currentStep >= Step.Complete ? "bg-blue" : "bg-gray-300"}`}
            />
          </div>
        </div>

        <SwitchTransition mode="out-in">
          <CSSTransition
            key={currentStep}
            nodeRef={nodeRef}
            timeout={300}
            classNames={{
              enter: styles.slideEnter,
              enterActive: styles.slideEnterActive,
              exit: styles.slideExit,
              exitActive: styles.slideExitActive,
            }}
          >
            <div ref={nodeRef} className="min-h-[400px] overflox-x-hidden">
              {currentStep === Step.Front && (
                <div>
                  <LicensePhoto
                    photo={frontPhoto}
                    onConfirmPhoto={handleFrontPhotoConfirm}
                  />
                  <div className="mt-4 text-center text-sm text-gray-600">
                    {t(
                      "waiver.licenseModal.frontInstructions",
                      "Please take a photo of the front of your license",
                    )}
                  </div>
                </div>
              )}

              {currentStep === Step.Back && (
                <div>
                  <LicensePhoto
                    photo={backPhoto}
                    onConfirmPhoto={handleBackPhotoConfirm}
                  />
                  <div className="mt-4 text-center text-sm text-gray-600">
                    {t(
                      "waiver.licenseModal.backInstructions",
                      "Please take a photo of the back of your license",
                    )}
                  </div>
                </div>
              )}

              {currentStep === Step.Complete && (
                <div>
                  <div className="grid grid-cols-2 gap-4 mb-6">
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 mb-2">
                        {t("waiver.licenseModal.frontTitle", "Front License")}
                      </h4>
                      {frontPhoto && (
                        <img
                          src={URL.createObjectURL(frontPhoto)}
                          alt="Front license"
                          className="w-full h-auto rounded border"
                        />
                      )}
                    </div>
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 mb-2">
                        {t("waiver.licenseModal.backTitle", "Back License")}
                      </h4>
                      {backPhoto && (
                        <img
                          src={URL.createObjectURL(backPhoto)}
                          alt="Back license"
                          className="w-full h-auto rounded border"
                        />
                      )}
                    </div>
                  </div>
                  <div className="text-center text-sm text-gray-600 mb-6">
                    {t(
                      "waiver.licenseModal.reviewInstructions",
                      "Please review your photos before submitting",
                    )}
                  </div>
                </div>
              )}
            </div>
          </CSSTransition>
        </SwitchTransition>

        <div className="flex justify-between mt-6">
          {currentStep !== Step.Front && (
            <Button
              onClick={handleBack}
              design="outline-blue"
              className="font-semibold"
              autoWidth
            >
              {t("common.back", "Back")}
            </Button>
          )}

          <div className="flex gap-2 ml-auto">
            {currentStep === Step.Complete ? (
              <Button
                onClick={handleComplete}
                disabled={!frontPhoto || !backPhoto}
                className="font-semibold"
                autoWidth
              >
                {t("waiver.licenseModal.submit", "Submit")}
              </Button>
            ) : (
              <Button
                onClick={handleClose}
                design="outline-blue"
                className="font-semibold"
                autoWidth
              >
                {t("common.cancel", "Cancel")}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LicensePhotoModal;
